import React, { useEffect, useState } from 'react';
import { useLocation, useParams, useNavigate } from 'react-router-dom';
import * as submasterService from '../../services/submaster.service';
import * as clientService from '../../services/client.service';
import { FaUser, FaBox, FaMapMarkerAlt, FaPhone, FaFileAlt, FaCheckCircle } from 'react-icons/fa';
import './BuyerLedger.css';

const TYPES = [
  { key: 'ledger-group', label: 'Ledger Group' },
  { key: 'currency', label: 'Currency' },
  { key: 'ingo-term', label: 'INGO Term' },
  { key: 'client-to', label: 'Client To' },
  { key: 'agency', label: 'Agency' },
  { key: 'payment-term', label: 'Payment Term' },
  { key: 'barcode-type', label: 'Barcode Type' },
];

const INITIAL_FORM = {
  ledgerGroup: '',
  currency: '',
  ingoTerm: '',
  clientTo: '',
  agency: '',
  paymentTerm: '',
  barcodeType: '',
  name: '',
  clientType: 'Domestic',
  clientId: '',
  contactAddress: '',
  contactCommercial: '',
  billingContact: '',
  email: '',
  distributionCenter: '',
  consigneeName: '',
  consigneeAddress: '',
  shippingName: '',
  shippingAddress: '',
  gstNo: '',
  tan: '',
  lut: '',
  notifyParty: '',
  alsoNotifyParty: '',
  placeOfReceipt: '',
  portOfDelivery: '',
  countryOfReceipt: '',
};

const BuyerLedgerCreation = () => {
  const params = useParams();
  const navigate = useNavigate();
  const editClientId = params.id || null;

  const [options, setOptions] = useState({});
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ ...INITIAL_FORM, _id: '' });
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (editClientId) {
      const loadClient = async () => {
        try {
          const client = await clientService.getClientById(editClientId);
          if (client) {
            const mapped = {
              ...INITIAL_FORM,
              ledgerGroup: client.ledger_group_id || '',
              currency: client.currency_id || '',
              ingoTerm: client.ingo_term_id || '',
              clientTo: client.client_to_id || '',
              agency: client.agency_id || '',
              paymentTerm: client.payment_term_id || '',
              barcodeType: client.barcode_type_id || '',
              name: client.name || '',
              clientType: client.client_type || 'Domestic',
              clientId: client.client_id || '',
              contactAddress: client.contact_address || '',
              contactCommercial: client.contact_commercial || '',
              billingContact: client.billing_contact || '',
              email: client.email || '',
              distributionCenter: client.distribution_center || '',
              consigneeName: client.consignee_name || '',
              consigneeAddress: client.consignee_address || '',
              shippingName: client.shipping_name || '',
              shippingAddress: client.shipping_address || '',
              gstNo: client.gst_no || '',
              tan: client.tan || '',
              lut: client.lut || '',
              notifyParty: client.notify_party || '',
              alsoNotifyParty: client.also_notify_party || '',
              placeOfReceipt: client.place_of_receipt || '',
              portOfDelivery: client.port_of_delivery || '',
              countryOfReceipt: client.country_of_receipt || '',
              _id: client.id,
            };
            setForm(mapped);
          }
        } catch (err) {
          setMessage({ type: 'danger', text: 'Failed to load client details.' });
          console.error(err);
        }
      };
      loadClient();
    } else {
      setForm({ ...INITIAL_FORM, _id: '' });
    }
  }, [editClientId]);

  useEffect(() => {
    let mounted = true;
    const loadAll = async () => {
      setLoading(true);
      try {
        const results = {};
        await Promise.all(
          TYPES.map(async (t) => {
            const items = await submasterService.getItems(t.key);
            results[t.key] = Array.isArray(items) ? items : items.data || [];
          })
        );
        if (!mounted) return;
        setOptions(results);
      } catch (e) {
        console.error('Failed to load submasters', e);
      } finally {
        if (mounted) setLoading(false);
      }
    };
    loadAll();
    return () => { mounted = false; };
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const validateForm = () => {
    const errs = {};
    if (!form.name || !form.name.trim()) errs.name = 'Name is required';
    if (!form.ledgerGroup) errs.ledgerGroup = 'Ledger Group is required';
    if (!form.currency) errs.currency = 'Currency is required';
    if (form.email && !/^\S+@\S+\.\S+$/.test(form.email)) errs.email = 'Enter a valid email address';
    if (form.clientType === 'Domestic') {
      if (!form.gstNo || !form.gstNo.trim()) errs.gstNo = 'GST No. is required for Domestic clients';
    }
    return errs;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage(null);
    const v = validateForm();
    if (Object.keys(v).length) {
      setErrors(v);
      setMessage({ type: 'danger', text: 'Please fix the errors highlighted below.' });
      return;
    }
    setSaving(true);
    try {
      // Map camelCase form to snake_case API format
      const payload = {
        ledger_group_id: form.ledgerGroup,
        currency_id: form.currency,
        ingo_term_id: form.ingoTerm,
        client_to_id: form.clientTo,
        agency_id: form.agency,
        payment_term_id: form.paymentTerm,
        barcode_type_id: form.barcodeType,
        client_id: form.clientId,
        name: form.name,
        client_type: form.clientType,
        contact_address: form.contactAddress,
        contact_commercial: form.contactCommercial,
        billing_contact: form.billingContact,
        email: form.email,
        distribution_center: form.distributionCenter,
        consignee_name: form.consigneeName,
        consignee_address: form.consigneeAddress,
        shipping_name: form.shippingName,
        shipping_address: form.shippingAddress,
        gst_no: form.gstNo,
        tan: form.tan,
        lut: form.lut,
        notify_party: form.notifyParty,
        also_notify_party: form.alsoNotifyParty,
        place_of_receipt: form.placeOfReceipt,
        port_of_delivery: form.portOfDelivery,
        country_of_receipt: form.countryOfReceipt,
      };
      const saved = await clientService.saveClient({ id: form._id || '', ...payload });
      const isEdit = !!form._id;
      setMessage({ type: 'success', text: isEdit ? `Client updated (${saved.client_id})` : `Client saved (Client ID: ${saved.client_id})` });
      if (!isEdit) {
        setForm({ ...INITIAL_FORM, _id: saved.id });
        setTimeout(() => {
          navigate(`/dashboard/crm-master/edit-client/${saved.id}`);
        }, 1500);
      }
    } catch (err) {
      setMessage({ type: 'danger', text: 'Failed to save. See console.' });
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="blc-page-bg">
      <div className="container-lg mt-2">

        <div className="mb-4">
          <div className="d-flex align-items-center gap-3 mb-2">
            <div className="blc-page-icon">
              <FaUser size={28} />
            </div>
            <div>
              <h1 className="mb-0 blc-page-title">{editClientId ? 'Edit Buyer Ledger' : 'Buyer Ledger Creation'}</h1>
              <p className="mb-0 text-muted">{editClientId ? 'Modify buyer ledger information' : 'Create and manage buyer ledger information'}</p>
            </div>
          </div>
        </div>

        {message && (
          <div className={`alert alert-${message.type} alert-dismissible fade show rounded-3 mb-4`} role="alert">
            <strong>{message.type === 'success' ? '✓ Success!' : '⚠ Error'}</strong> {message.text}
            <button type="button" className="btn-close" onClick={() => setMessage(null)}></button>
          </div>
        )}

        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>

            <div className="card border-0 shadow-sm mb-4 blc-card blc-card-primary">
              <div className="card-header bg-white border-0 pb-0">
                <h5 className="mb-0 blc-card-title blc-card-title-primary">
                  <FaUser className="me-2" />
                  Client Configuration
                </h5>
              </div>
              <div className="card-body">
                <div className="row">
                  <div className="col-lg-6 mb-3">
                    <label className="form-label fw-semibold text-dark">Client Type <span className="text-danger">*</span></label>
                    <div className="d-flex gap-3">
                      <div className="form-check">
                        <input className="form-check-input" type="radio" name="clientType" id="clientDomestic" value="Domestic" checked={form.clientType === 'Domestic'} onChange={handleChange} />
                        <label className="form-check-label" htmlFor="clientDomestic">Domestic</label>
                      </div>
                      <div className="form-check">
                        <input className="form-check-input" type="radio" name="clientType" id="clientExport" value="Export" checked={form.clientType === 'Export'} onChange={handleChange} />
                        <label className="form-check-label" htmlFor="clientExport">Export</label>
                      </div>
                    </div>
                  </div>
                  <div className="col-lg-6 mb-3">
                    <label className="form-label fw-semibold text-dark">Client Code <span className="text-danger">*</span></label>
                    <input
                      name="clientId"
                      value={form.clientId}
                      onChange={handleChange}
                      readOnly={!!form._id}
                      className={`form-control form-control-lg blc-input ${form._id ? 'bg-light' : ''}`}
                      placeholder="Enter Client Code"
                      title={form._id ? 'Client code cannot be changed after save' : ''}
                    />
                    {form._id && <small className="text-muted">Client code cannot be modified once saved.</small>}
                  </div>
                </div>
                <div className="row">
                  <div className="col-lg-12 mb-3">
                    <label className="form-label fw-semibold text-dark">Client Name <span className="text-danger">*</span></label>
                    <input
                      name="name"
                      value={form.name}
                      onChange={handleChange}
                      className={`form-control form-control-lg blc-input ${errors.name ? 'is-invalid' : ''}`}
                      placeholder="Enter client/buyer name"
                    />
                    {errors.name && <div className="invalid-feedback d-block">{errors.name}</div>}
                  </div>
                </div>
              </div>
            </div>

            <div className="card border-0 shadow-sm mb-4 blc-card blc-card-blue">
              <div className="card-header bg-white border-0 pb-0">
                <h5 className="mb-0 blc-card-title blc-card-title-blue">
                  <FaBox className="me-2" />
                  Ledger Configuration
                </h5>
              </div>
              <div className="card-body">
                <div className="row">
                  {TYPES.map((t) => (
                    <div className="col-lg-4 mb-3" key={t.key}>
                      <label className="form-label fw-semibold text-dark">{t.label} <span className="text-danger">*</span></label>
                      <select
                        name={mapKeyToField(t.key)}
                        value={form[mapKeyToField(t.key)]}
                        onChange={handleChange}
                        className={`form-select form-select-lg blc-input ${errors[mapKeyToField(t.key)] ? 'is-invalid' : ''}`}
                      >
                        <option value="">Select {t.label}</option>
                        {(options[t.key] || []).map((opt) => (
                          <option key={opt.id} value={opt.id}>{opt.name}</option>
                        ))}
                      </select>
                      {errors[mapKeyToField(t.key)] && <div className="invalid-feedback d-block">{errors[mapKeyToField(t.key)]}</div>}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="card border-0 shadow-sm mb-4 blc-card blc-card-teal">
              <div className="card-header bg-white border-0 pb-0">
                <h5 className="mb-0 blc-card-title blc-card-title-teal">
                  <FaMapMarkerAlt className="me-2" />
                  Shipping Details
                </h5>
              </div>
              <div className="card-body">
                <div className="row">
                  {[
                    { name: 'distributionCenter', label: 'Distribution Center / Warehouse', placeholder: 'Enter distribution center code' },
                    { name: 'placeOfReceipt',     label: 'Place of Receipt',                placeholder: 'Enter place of receipt' },
                    { name: 'consigneeName',       label: 'Consignee Name',                  placeholder: 'Enter consignee name' },
                    { name: 'consigneeAddress',    label: 'Consignee Address',               placeholder: 'Enter consignee address' },
                    { name: 'shippingName',        label: 'Shipping Name',                   placeholder: 'Enter shipping name' },
                    { name: 'shippingAddress',     label: 'Shipping Address',                placeholder: 'Enter shipping address' },
                  ].map((field) => (
                    <div className="col-lg-6 mb-3" key={field.name}>
                      <label className="form-label fw-semibold text-dark">{field.label}</label>
                      <input
                        name={field.name}
                        value={form[field.name]}
                        onChange={handleChange}
                        className="form-control form-control-lg blc-input"
                        placeholder={field.placeholder}
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="card border-0 shadow-sm mb-4 blc-card blc-card-red">
              <div className="card-header bg-white border-0 pb-0">
                <h5 className="mb-0 blc-card-title blc-card-title-red">
                  <FaPhone className="me-2" />
                  Contact Details
                </h5>
              </div>
              <div className="card-body">
                <div className="row">
                  <div className="col-lg-6 mb-3">
                    <label className="form-label fw-semibold text-dark">Address</label>
                    <textarea name="contactAddress" value={form.contactAddress} onChange={handleChange} className="form-control blc-input" rows="3" placeholder="Enter contact address" />
                  </div>
                  <div className="col-lg-6 mb-3">
                    <label className="form-label fw-semibold text-dark">Commercial Contact</label>
                    <input name="contactCommercial" value={form.contactCommercial} onChange={handleChange} className="form-control form-control-lg blc-input" placeholder="Enter commercial contact" />
                  </div>
                  <div className="col-lg-6 mb-3">
                    <label className="form-label fw-semibold text-dark">Billing Contact</label>
                    <input name="billingContact" value={form.billingContact} onChange={handleChange} className="form-control form-control-lg blc-input" placeholder="Enter billing contact" />
                  </div>
                  <div className="col-lg-6 mb-3">
                    <label className="form-label fw-semibold text-dark">Email <span className="text-danger">*</span></label>
                    <input name="email" value={form.email} onChange={handleChange} className={`form-control form-control-lg blc-input ${errors.email ? 'is-invalid' : ''}`} placeholder="email@example.com" />
                    {errors.email && <div className="invalid-feedback d-block">{errors.email}</div>}
                  </div>
                </div>
              </div>
            </div>

            <div className="card border-0 shadow-sm mb-4 blc-card blc-card-orange">
              <div className="card-header bg-white border-0 pb-0">
                <h5 className="mb-0 blc-card-title blc-card-title-orange">
                  <FaFileAlt className="me-2" />
                  {form.clientType === 'Domestic' ? 'Domestic Details' : 'Export Details'}
                </h5>
              </div>
              <div className="card-body">
                <div className="row">
                  {form.clientType === 'Domestic' ? (
                    <>
                      <div className="col-lg-4 mb-3">
                        <label className="form-label fw-semibold text-dark">GST No. <span className="text-danger">*</span></label>
                        <input name="gstNo" value={form.gstNo} onChange={handleChange} className={`form-control form-control-lg blc-input ${errors.gstNo ? 'is-invalid' : ''}`} placeholder="XXXXXXXXXXXXXXXXXXX" />
                        {errors.gstNo && <div className="invalid-feedback d-block">{errors.gstNo}</div>}
                      </div>
                      <div className="col-lg-4 mb-3">
                        <label className="form-label fw-semibold text-dark">TAN</label>
                        <input name="tan" value={form.tan} onChange={handleChange} className="form-control form-control-lg blc-input" placeholder="Tax Account Number" />
                      </div>
                      <div className="col-lg-4 mb-3">
                        <label className="form-label fw-semibold text-dark">LUT</label>
                        <input name="lut" value={form.lut} onChange={handleChange} className="form-control form-control-lg blc-input" placeholder="Letter of Undertaking" />
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="col-lg-6 mb-3">
                        <label className="form-label fw-semibold text-dark">Notify Party</label>
                        <input name="notifyParty" value={form.notifyParty} onChange={handleChange} className="form-control form-control-lg blc-input" placeholder="Notify party name" />
                      </div>
                      <div className="col-lg-6 mb-3">
                        <label className="form-label fw-semibold text-dark">Also Notify Party</label>
                        <input name="alsoNotifyParty" value={form.alsoNotifyParty} onChange={handleChange} className="form-control form-control-lg blc-input" placeholder="Also notify party name" />
                      </div>
                      <div className="col-lg-6 mb-3">
                        <label className="form-label fw-semibold text-dark">Port of Delivery</label>
                        <input name="portOfDelivery" value={form.portOfDelivery} onChange={handleChange} className="form-control form-control-lg blc-input" placeholder="Port of delivery" />
                      </div>
                      <div className="col-lg-6 mb-3">
                        <label className="form-label fw-semibold text-dark">Country of Receipt</label>
                        <select name="countryOfReceipt" value={form.countryOfReceipt} onChange={handleChange} className="form-select form-select-lg blc-input">
                          <option value="">Select Country</option>
                          {COUNTRIES.map((c) => (
                            <option key={c.code} value={c.code}>{c.name}</option>
                          ))}
                        </select>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>

            <div className="mb-4 d-flex justify-content-between">
              <button type="submit" disabled={saving} className="btn btn-lg blc-submit-btn">
                <FaCheckCircle className="me-2" />
                {saving ? 'Saving...' : 'Save Ledger'}
              </button>
            </div>

          </form>
        )}
      </div>
    </div>
  );
};

function mapKeyToField(key) {
  switch (key) {
    case 'ledger-group':  return 'ledgerGroup';
    case 'currency':      return 'currency';
    case 'ingo-term':     return 'ingoTerm';
    case 'client-to':     return 'clientTo';
    case 'agency':        return 'agency';
    case 'payment-term':  return 'paymentTerm';
    case 'barcode-type':  return 'barcodeType';
    default:              return key.replace(/-/g, '_');
  }
}

const COUNTRIES = [
  { code: 'IN', name: 'India' },
  { code: 'US', name: 'United States' },
  { code: 'CN', name: 'China' },
  { code: 'GB', name: 'United Kingdom' },
  { code: 'AE', name: 'United Arab Emirates' },
  { code: 'DE', name: 'Germany' },
  { code: 'FR', name: 'France' },
  { code: 'SG', name: 'Singapore' },
];

export default BuyerLedgerCreation;
